import {
  Align,
  Bar,
  Button,
  ButtonList,
  Checkbox,
  Justify,
  Label,
  Position,
  Row,
  Size,
  Theme,
} from '@busybusy/webapp-react-ui';
import { TimeCardIcon } from 'assets/icons';
import { Badge } from 'components';
import FormattedEmployeeName from 'components/domain/member/FormattedEmployeeName/FormattedEmployeeName';
import IconButton from 'components/foundation/buttons/IconButton/IconButton';
import MoreButton from 'components/foundation/buttons/MoreButton/MoreButton';
import { useOrganization, usePermissions } from 'hooks';
import _, { isNil } from 'lodash';
import * as React from 'react';
import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IReduxState } from 'store/reducers';
import { updateCheckedMemberIds } from 'store/timesheets/Timesheets';
import { secondsToHHmmString } from 'utils/dateUtils';
import { useFeatureFlags } from 'utils/features';
import LocalStore from 'utils/localStorageUtils';
import { t } from 'utils/localize';
import { formatTime } from 'utils/timeUtils';
import './TimeCardReportMemberHeader.scss';
import { Nullable } from 'types/util/Nullable';
import { Member } from '__generated__/graphql';

export interface ITimeCardReportMemberHeaderProps<
  T extends Pick<Member, 'firstName' | 'lastName' | 'id' | 'archivedOn'>,
> {
  member: T;
  memberSeconds: Nullable<number>;
  onViewEntries: (member: T) => void;
  onViewSignOff: (member: T) => void;
  isExpanded: boolean;
  showDecimalFormat?: boolean;
}

const TimeCardReportMemberHeader = <T extends Pick<Member, 'firstName' | 'lastName' | 'id' | 'archivedOn'>>(
  props: ITimeCardReportMemberHeaderProps<T>
) => {
  const { member, onViewEntries, onViewSignOff, showDecimalFormat, memberSeconds } = props;
  const isPro = useFeatureFlags('PRO');
  const dispatch = useDispatch();
  const checkedItems = useSelector<IReduxState, string[]>((state) => state.timesheet.checkedMemberIds);
  const org = useOrganization();
  const isDailySignOffEnabled = org.safetySignature || org.timeAccuracy || org.breakPolicy;
  const { hasPermissionToManage } = usePermissions();
  const canManage = useRef<boolean>(hasPermissionToManage(member, 'manageTimeEntries'));
  const isArchived = useRef<boolean>(!isNil(member.archivedOn));
  const tableRowHeight = LocalStore.get('tableRowHeight') ?? 'p-4';

  const formattedTotal = getTotal();

  function getTotal() {
    if (!memberSeconds) {
      return '--:--';
    }

    if (showDecimalFormat) {
      return formatTime({
        type: 'DECIMAL',
        seconds: memberSeconds,
        places: 2,
      });
    }

    return secondsToHHmmString(memberSeconds);
  }

  function viewTimeEntries() {
    onViewEntries(member);
  }

  function renderDropdownContent(closeDropdown: () => void) {
    function handleViewSignOffDropdown(event: React.MouseEvent) {
      closeDropdown();
      event.stopPropagation();
      event.preventDefault();
      onViewSignOff(member);
    }

    return (
      <ButtonList>
        <Button onClick={handleViewSignOffDropdown}>{t('View Daily Sign-Off')}</Button>
      </ButtonList>
    );
  }

  const handleCheckboxChange = (checked: boolean) => {
    if (checked && !checkedItems.some((id) => id === member.id)) {
      dispatch(updateCheckedMemberIds([...checkedItems, member.id]));
    } else if (!checked && checkedItems.some((id) => id === member.id)) {
      dispatch(updateCheckedMemberIds(_.filter(checkedItems, (id) => id !== member.id)));
    }
  };

  return (
    <div className="time-card-employee-name-bar">
      <Bar justify={Justify.SPACE_BETWEEN} className={`bar-height-unset ${tableRowHeight}`}>
        <div className="left-container ">
          {canManage.current && !isArchived.current ? (
            <>
              <Checkbox
                className="no-print"
                onChange={handleCheckboxChange}
                checked={_.some(checkedItems, (id) => member.id === id)}
              />

              <div>
                <Label className={'employee-name-title pl-4'}>
                  <FormattedEmployeeName firstName={member.firstName ?? ''} lastName={member.lastName ?? ''} />
                </Label>
              </div>
            </>
          ) : (
            <Row align={Align.CENTER} justify={Justify.SPACE_BETWEEN}>
              <Label className="pt-1">
                <FormattedEmployeeName firstName={member.firstName ?? ''} lastName={member.lastName ?? ''} />
              </Label>
              {isArchived.current ? (
                <Badge key={member.id + '-archived'} className={'ml-6'}>
                  {t('ARCHIVED')}
                </Badge>
              ) : undefined}
            </Row>
          )}
        </div>

        <div className="right-container">
          <Label
            className={`total-label pr-4 ${tableRowHeight === 'p-1' || tableRowHeight === 'p-2' ? 'pt-1' : 'pt-2'}`}
          >
            {formattedTotal}
          </Label>

          <IconButton
            className={`no-print  ${tableRowHeight === 'p-1' || tableRowHeight === 'p-2' ? 'small-icon' : ''}`}
            svg={TimeCardIcon}
            onClick={viewTimeEntries}
            theme={Theme.LIGHT}
            buttonSize={Size.SMALL}
            tooltipLabel={t('View Time Entries')}
          />

          {isPro && isDailySignOffEnabled && (
            <MoreButton
              className={`no-print  ${tableRowHeight === 'p-1' || tableRowHeight === 'p-2' ? 'small-icon' : 'pb-2'}`}
              position={Position.BOTTOM_END}
              renderContent={renderDropdownContent}
            />
          )}
        </div>
      </Bar>
    </div>
  );
};

export default TimeCardReportMemberHeader;
